import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const FeatureToggleContext = createContext({
  features: {},
  isEnabled: () => true,
  loading: true,
  clubSlug: null
});

export const useFeatureToggle = () => useContext(FeatureToggleContext);

const API = process.env.REACT_APP_BACKEND_URL;

export function FeatureToggleProvider({ clubSlug, children }) {
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clubSlug) {
      setFeatures({});
      setLoading(false);
      return;
    }

    const fetchToggles = async () => {
      try {
        const res = await fetch(`${API}/api/feature-toggles/club/${clubSlug}`);
        if (res.ok) {
          const data = await res.json();
          setFeatures(data.features || {});
        }
      } catch (err) {
        console.error('Failed to fetch feature toggles:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchToggles();
  }, [clubSlug]);

  const isEnabled = useCallback((featureKey) => {
    // If no club context or feature not defined, default to enabled
    if (!clubSlug || !(featureKey in features)) return true;
    return features[featureKey];
  }, [features, clubSlug]);

  return (
    <FeatureToggleContext.Provider value={{ features, isEnabled, loading, clubSlug }}>
      {children}
    </FeatureToggleContext.Provider>
  );
}

/**
 * Gate component — only renders children if the feature is enabled.
 * Shows nothing (or fallback) when disabled.
 */
export function FeatureGate({ feature, children, fallback = null }) {
  const { isEnabled } = useFeatureToggle();
  if (!isEnabled(feature)) return fallback;
  return children;
}
